import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../index';
import { getApiBaseUrl } from '../../utils/apiConfig';

export interface Payee {
  id: number;
  name: string;
  avatar: string;
  email: string;
  accountNumber: string;
  /** When set, used for bank transfers; otherwise accountNumber is treated as IBAN for TrueLayer */
  iban?: string;
  bank: string;
  lastPayment: number;
  lastPaymentDate: string;
  totalPayments: number;
  totalAmount: number;
  category: string;
  isFavorite: boolean;
}

interface PayeeState {
  payees: Payee[];
  isLoading: boolean;
  error: string | null;
}

const initialState: PayeeState = {
  payees: [],
  isLoading: false,
  error: null,
};

export const fetchPayees = createAsyncThunk<Payee[], void, { state: RootState }>(
  'payees/fetchPayees',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState().auth;
      const token = state.token;
      const authType = state.type;
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      // Only add Authorization header for JWT auth
      if (token && authType === 'JWT') {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${getApiBaseUrl()}/payees`, {
        headers,
        credentials: authType === 'SESSION' ? 'include' : 'omit',
      });
      const json = await response.json();
      if (!response.ok) {
        if (json.errors) return rejectWithValue(json.errors.join(', '));
        return rejectWithValue(json.message || 'Failed to fetch payees');
      }
      return json.data || [];
    } catch (err: any) {
      return rejectWithValue(err.message || 'Unknown error');
    }
  }
);

export const addPayee = createAsyncThunk<Payee, Omit<Payee, 'id'>, { state: RootState }>(
  'payees/addPayee',
  async (payeeData, { getState, rejectWithValue }) => {
    try {
      const state = getState().auth;
      const token = state.token;
      const authType = state.type;
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      // Only add Authorization header for JWT auth
      if (token && authType === 'JWT') {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${getApiBaseUrl()}/payees`, {
        method: 'POST',
        headers,
        credentials: authType === 'SESSION' ? 'include' : 'omit',
        body: JSON.stringify(payeeData),
      });
      const json = await response.json();
      if (!response.ok) {
        if (json.errors) return rejectWithValue(json.errors.join(', '));
        return rejectWithValue(json.message || 'Failed to add payee');
      }
      return json.data;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Unknown error');
    }
  }
);

export const deletePayee = createAsyncThunk<number, number, { state: RootState }>(
  'payees/deletePayee',
  async (payeeId, { getState, rejectWithValue }) => {
    try {
      const state = getState().auth;
      const token = state.token;
      const authType = state.type;
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      // Only add Authorization header for JWT auth
      if (token && authType === 'JWT') {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${getApiBaseUrl()}/payees/${payeeId}`, {
        method: 'DELETE',
        headers,
        credentials: authType === 'SESSION' ? 'include' : 'omit',
      });
      if (!response.ok) {
        const json = await response.json();
        if (json.errors) return rejectWithValue(json.errors.join(', '));
        return rejectWithValue(json.message || 'Failed to delete payee');
      }
      return payeeId;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Unknown error');
    }
  }
);

const payeeSlice = createSlice({
  name: 'payees',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPayees.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPayees.fulfilled, (state, action: PayloadAction<Payee[]>) => {
        state.isLoading = false;
        state.payees = action.payload;
      })
      .addCase(fetchPayees.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(addPayee.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addPayee.fulfilled, (state, action: PayloadAction<Payee>) => {
        state.isLoading = false;
        state.payees.push(action.payload);
      })
      .addCase(addPayee.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(deletePayee.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deletePayee.fulfilled, (state, action: PayloadAction<number>) => {
        state.isLoading = false;
        state.payees = state.payees.filter(p => p.id !== action.payload);
      })
      .addCase(deletePayee.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export default payeeSlice.reducer; 